import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { COUNTRIES, getCountryByCode, formatPhoneNumber, DEFAULT_COUNTRY_CODE } from '../../config/countriesConfig';

const PhoneInput = ({
  value,
  onChange,
  countryCode,
  onCountryChange,
  label = 'Phone Number',
  required = false,
  error,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selectedCountry = getCountryByCode(countryCode || DEFAULT_COUNTRY_CODE);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePhoneChange = (e) => {
    const input = e.target.value;
    const formatted = formatPhoneNumber(input, selectedCountry.format);
    onChange(formatted);
  };

  const handleCountrySelect = (country) => {
    onCountryChange(country.code);
    setIsOpen(false);
    // Clear phone when country changes
    onChange('');
  };

  // Generate placeholder based on country format
  const placeholder = selectedCountry.format.replace(/#/g, '0');

  return (
    <div className="space-y-1.5 min-w-0">
      {label && (
        <label className="text-xs font-medium text-gray-600">
          {label}
          {required && <span className="text-[#FF8C00] ml-1">*</span>}
        </label>
      )}

      <div className="flex min-w-0" ref={dropdownRef}>
        {/* Country Code Selector */}
        <div className="relative flex-shrink-0">
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className={`h-11 px-2 sm:px-3 bg-white border border-r-0 rounded-l-lg flex items-center gap-1 hover:bg-gray-50 transition-colors ${
              error ? 'border-[#FF8C00]' : 'border-gray-200'
            }`}
            data-testid="phone-country-selector"
          >
            <span className="text-base sm:text-lg">{selectedCountry.flag}</span>
            <span className="text-xs sm:text-sm text-black font-medium">{selectedCountry.dialCode}</span>
            <ChevronDown className={`h-3 w-3 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Country Dropdown */}
          {isOpen && (
            <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 w-64 max-h-48 overflow-y-auto">
              {COUNTRIES.map((country) => (
                <button
                  key={country.code}
                  type="button"
                  onClick={() => handleCountrySelect(country)}
                  className={`w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-gray-50 transition-colors ${
                    countryCode === country.code ? 'bg-[#FF8C00]/5' : ''
                  }`}
                  data-testid={`phone-country-${country.code.toLowerCase()}`}
                >
                  <span className="text-lg">{country.flag}</span>
                  <span className="text-sm text-black flex-1">{country.name}</span>
                  <span className="text-xs text-gray-400">{country.dialCode}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Phone Number Input */}
        <input
          type="tel"
          value={value}
          onChange={handlePhoneChange}
          placeholder={placeholder}
          className={`flex-1 min-w-0 h-11 px-2 sm:px-3 bg-white border rounded-r-lg text-sm text-black placeholder:text-gray-400 focus:outline-none transition-colors ${
            error
              ? 'border-[#FF8C00] focus:ring-2 focus:ring-[#FF8C00]/20'
              : 'border-gray-200 focus:border-[#FF8C00] focus:ring-2 focus:ring-[#FF8C00]/20'
          }`}
          data-testid="phone-input"
        />
      </div>

      {error && (
        <p className="text-xs text-[#FF8C00]">{error}</p>
      )}
    </div>
  );
};

export default PhoneInput;
